(() => {
  // "X Colours" on product cards:
  // 1) counts in-stock products (shopper's market) sharing the same "DESIGN: ..." tag
  //    using the design-colour-count section, and
  // 2) on click, opens a side drawer listing those products, rendered by the
  //    design-colours-results section.
  const cache = {};
  const resultsCache = {};
  // Cache per market/country so switching country never shows another market's count
  const market = (window.Shopify && (window.Shopify.country || '')) + '-' + ((window.Shopify && window.Shopify.locale) || '');
  const storageKey = 'bm-design-counts-v6-' + market;
  let stored = {};
  try {
    stored = JSON.parse(sessionStorage.getItem(storageKey) || '{}');
  } catch (e) {}

  const save = () => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(stored));
    } catch (e) {}
  };

  const searchPath = () => {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : '/';
    return root + 'search';
  };

  // design -> tag-filtered collection path, e.g. /collections/all/design-cloudpack-backpack
  const designPaths = {};
  const designUrl = (design, sectionId) => {
    const path = designPaths[design] || searchPath();
    const url = new URL(path, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    // When previewing an unpublished theme (Canvas, theme editor, preview links),
    // make sure the request is served by that theme, not the live one.
    const theme = window.Shopify && window.Shopify.theme;
    if (theme && theme.id && theme.role !== 'main') {
      url.searchParams.set('preview_theme_id', theme.id);
    }
    return url.toString();
  };

  // Performance: at most 3 count lookups at once, so they never crowd out images and scripts
  const MAX_PARALLEL = 3;
  let active = 0;
  const waiting = [];
  const pump = () => {
    while (active < MAX_PARALLEL && waiting.length) {
      const job = waiting.shift();
      active++;
      fetch(job.url, { credentials: 'same-origin' })
        .then(job.resolve, () => job.resolve(null))
        .finally(() => {
          active--;
          pump();
        });
    }
  };
  const queueFetch = (url) =>
    new Promise((resolve) => {
      waiting.push({ url, resolve });
      pump();
    });

  // Resolves to the list of in-stock product IDs for a design (bundles excluded)
  const getIds = (design) => {
    if (Array.isArray(stored[design])) return Promise.resolve(stored[design]);
    if (cache[design]) return cache[design];
    cache[design] = queueFetch(designUrl(design, 'design-colour-count'))
      .then((response) => (response && response.ok ? response.text() : ''))
      .then((html) => {
        const match = html.match(/data-design-ids="([\d,]*)"/);
        if (!match) {
          // Lookup failed: don't remember it, so the next page view tries again
          delete cache[design];
          return [];
        }
        const ids = match[1] ? match[1].split(',') : [];
        stored[design] = ids;
        save();
        return ids;
      })
      .catch(() => {
        delete cache[design];
        return [];
      });
    return cache[design];
  };

  const getResults = (design) => {
    if (resultsCache[design]) return resultsCache[design];
    resultsCache[design] = fetch(designUrl(design, 'design-colours-results'), { credentials: 'same-origin' })
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const grid = doc.querySelector('[data-design-drawer-results]');
        return grid ? grid.outerHTML : '';
      })
      .catch(() => {
        delete resultsCache[design];
        return '';
      });
    return resultsCache[design];
  };

  // ---- Drawer ----
  const drawer = document.querySelector('[data-design-drawer]');
  let lastTrigger = null;
  let hideTimer = null;

  const focusable = () =>
    drawer.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])');

  const closeDrawer = () => {
    if (!drawer || drawer.hidden) return;
    drawer.classList.remove('is-open');
    document.documentElement.classList.remove('design-drawer-open');
    hideTimer = setTimeout(() => {
      drawer.hidden = true;
    }, 300);
    if (lastTrigger) lastTrigger.focus();
  };

  const openDrawer = (trigger) => {
    if (!drawer) return false;
    clearTimeout(hideTimer);
    lastTrigger = trigger;
    const design = trigger.dataset.designColours;
    if (trigger.dataset.designUrl) designPaths[design] = trigger.dataset.designUrl;
    const name = trigger.dataset.designName || '';
    const countText = trigger.querySelector('[data-design-colours-text]');
    const title = drawer.querySelector('[data-design-drawer-title]');
    const body = drawer.querySelector('[data-design-drawer-body]');
    title.textContent = [name, countText ? countText.textContent : ''].filter(Boolean).join(' / ');
    body.innerHTML = '<div class="design-drawer__loading" aria-live="polite"><span class="design-drawer__spinner"></span></div>';

    drawer.hidden = false;
    requestAnimationFrame(() => {
      drawer.classList.add('is-open');
      document.documentElement.classList.add('design-drawer-open');
      drawer.querySelector('.design-drawer__panel').focus();
    });

    getResults(design).then((html) => {
      if (html) {
        body.innerHTML = html;
      } else {
        // Fall back to the full results page if the drawer content can't load
        window.location.href = trigger.href;
      }
    });
    return true;
  };

  if (drawer) {
    drawer.addEventListener('click', (event) => {
      if (event.target.closest('[data-design-drawer-close]')) closeDrawer();
    });
    // Close on any tap/click outside the panel, caught at page level first so an app
    // layer sitting over the dimmed area (or stopping the click) can't block it.
    const panel = drawer.querySelector('.design-drawer__panel');
    document.addEventListener(
      'click',
      (event) => {
        if (drawer.hidden || !drawer.classList.contains('is-open')) return;
        if (panel && panel.contains(event.target)) return;
        closeDrawer();
      },
      true
    );
    document.addEventListener('keydown', (event) => {
      if (drawer.hidden) return;
      if (event.key === 'Escape') {
        closeDrawer();
        return;
      }
      if (event.key === 'Tab') {
        const items = Array.from(focusable());
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    });
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-design-colours]');
      if (!trigger || drawer.contains(trigger)) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey) return; // allow open in new tab
      if (openDrawer(trigger)) event.preventDefault();
    });
  }

  // ---- Counts ----
  // Performance: only look up counts for cards that are on (or about to be on) screen
  const showCount = (el) => {
      const design = el.dataset.designColours;
      if (!design) return;
      if (el.dataset.designUrl) designPaths[design] = el.dataset.designUrl;
      getIds(design).then((ids) => {
        // Only show when at least one OTHER option is in stock
        // (a sold-out CORE RANGE card still links to its in-stock siblings)
        const others = ids.filter((id) => id !== String(el.dataset.productId));
        if (others.length < 1) return;
        const count = ids.length;
        const label = count === 1 ? el.dataset.labelOne || 'Colour' : el.dataset.label || 'Colours';
        const text = el.querySelector('[data-design-colours-text]');
        if (text) text.textContent = `${count} ${label}`;
        el.hidden = false;
      });
  };
  const watched = new Map();
  const observer =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              observer.unobserve(entry.target);
              const links = watched.get(entry.target) || [];
              watched.delete(entry.target);
              links.forEach(showCount);
            });
          },
          { rootMargin: '400px 0px' }
        )
      : null;
  const render = () => {
    document.querySelectorAll('[data-design-colours]:not([data-design-ready])').forEach((el) => {
      if (drawer && drawer.contains(el)) return;
      el.setAttribute('data-design-ready', '');
      // The link itself starts hidden, so watch its (visible) card instead
      const target = el.closest('.product-card') || el.parentElement;
      if (observer && target) {
        if (!watched.has(target)) {
          watched.set(target, []);
          observer.observe(target);
        }
        watched.get(target).push(el);
      } else {
        showCount(el);
      }
    });
  };

  let queued = false;
  const queueRender = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      render();
    });
  };

  render();
  // Re-run when more products load (load more, filtering, sorting)
  new MutationObserver(queueRender).observe(document.body, { childList: true, subtree: true });
})();
